/**
 * EnforceOrganization Component
 *
 * Ensures that authenticated users have an organization membership.
 * If user has no organization, redirects to organization selection/creation flow.
 * Enforces organization-scoped routing for all protected routes.
 */

"use client";

import { api } from "@agroproj/backend/convex/_generated/api";
import { useAuth } from "@clerk/nextjs";
import { useConvexAuth, useMutation, useQuery } from "convex/react";
import type { Route } from "next";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { SubscriptionRequiredDialog } from "@/components/billing/subscription-required-dialog";
import { PageWrapperLoading } from "@/components/page-wrapper";
import { buildOrganizationPath, getRelativeOrganizationPath, isPathWithinOrganization } from "@/lib/organization-path";
import { capturePosthogEvent } from "@/lib/posthog";

// Thrown errors (e.g. the auth context still settling right after org creation) can be
// transient, so retry a bounded number of times with exponential backoff before giving up.
// Deterministic `success: false` results are never retried.
const MAX_FREE_SUBSCRIPTION_ATTEMPTS = 3;
const FREE_SUBSCRIPTION_RETRY_BASE_DELAY_MS = 500;
interface EnforceOrganizationProps {
  children: ReactNode;
  /** Shown while auth and membership resolve. Defaults to the generic page skeleton. */
  loadingFallback?: ReactNode;
}
export function EnforceOrganization({
  children,
  loadingFallback = <PageWrapperLoading />
}: EnforceOrganizationProps) {
  const {
    isLoaded: isClerkLoaded,
    isSignedIn
  } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const organizationStatus = useQuery(api.check_membership.hasOrganization);
  const ensureFreeSubscription = useMutation(api.payments.mutations.ensureFreeSubscriptionForActiveOrganization);
  const [retryVersion, setRetryVersion] = useState(0);
  const [freeSubscriptionFailedSlug, setFreeSubscriptionFailedSlug] = useState<string | null>(null);
  const freeSubscriptionAttemptSlugRef = useRef<string | null>(null);
  const freeSubscriptionRetryRef = useRef<{
    slug: string | null;
    attempts: number;
  }>({
    slug: null,
    attempts: 0
  });
  const freeSubscriptionRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isOnboardingRoute = pathname.startsWith("/onboarding");
  const isAppRoute = pathname === "/app";
  const isAuthenticated = organizationStatus?.isAuthenticated ?? false;
  const isAuthBootstrapping = isClerkLoaded && isSignedIn && !isAuthenticated;
  const isLoading = !isClerkLoaded || organizationStatus === undefined || isAuthBootstrapping;
  const hasOrganization = organizationStatus?.hasOrganization ?? false;
  const activeOrganizationSlug = organizationStatus?.activeOrganizationSlug ?? null;
  const billingStatus = organizationStatus?.billingStatus ?? "none";
  const hasActiveSubscription = organizationStatus?.hasActiveSubscription ?? false;
  const organizationDashboardPath = activeOrganizationSlug !== null ? buildOrganizationPath(activeOrganizationSlug, "/") : null;
  const isOrganizationDashboardRoute = organizationDashboardPath !== null && pathname === organizationDashboardPath;
  const isBillingRequiredRoute = activeOrganizationSlug !== null && pathname === `/${activeOrganizationSlug}/billing-required`;
  const isBillingSettingsRoute = activeOrganizationSlug !== null && pathname === `/${activeOrganizationSlug}/settings/billing`;
  const isUnpaidAllowedRoute = isOrganizationDashboardRoute || isBillingSettingsRoute;
  // Reflect "we will attempt provisioning" rather than "we have attempted", so the first
  // committed render (and the redirect effect that reads this value) already gates on it.
  // Basing it on freeSubscriptionAttemptSlugRef instead would leave a stale `false` on mount,
  // flashing the dashboard/dialog and prematurely redirecting deep routes before the free
  // subscription resolves.
  const isWaitingForFreeSubscription = isSignedIn && hasOrganization && activeOrganizationSlug !== null && !hasActiveSubscription && billingStatus === "none" && freeSubscriptionFailedSlug !== activeOrganizationSlug;
  useEffect(() => {
    if (isLoading || !isSignedIn || !hasOrganization || !activeOrganizationSlug || hasActiveSubscription || billingStatus !== "none" || freeSubscriptionAttemptSlugRef.current === activeOrganizationSlug || freeSubscriptionFailedSlug === activeOrganizationSlug) {
      return;
    }
    if (freeSubscriptionRetryTimerRef.current) {
      clearTimeout(freeSubscriptionRetryTimerRef.current);
      freeSubscriptionRetryTimerRef.current = null;
    }
    if (freeSubscriptionRetryRef.current.slug !== activeOrganizationSlug) {
      freeSubscriptionRetryRef.current = {
        slug: activeOrganizationSlug,
        attempts: 0
      };
    }
    freeSubscriptionAttemptSlugRef.current = activeOrganizationSlug;
    ensureFreeSubscription({}).then(result => {
      // A `success: false` result is deterministic (not authorized, ineligible billing
      // status, or no free plan available); retrying would not change the outcome.
      if (!result.success) {
        setFreeSubscriptionFailedSlug(activeOrganizationSlug);
      }
    }).catch(error => {
      console.error("Failed to ensure free subscription:", error);
      const attempts = freeSubscriptionRetryRef.current.attempts + 1;
      freeSubscriptionRetryRef.current = {
        slug: activeOrganizationSlug,
        attempts
      };
      if (attempts >= MAX_FREE_SUBSCRIPTION_ATTEMPTS) {
        setFreeSubscriptionFailedSlug(activeOrganizationSlug);
        return;
      }
      const delay = FREE_SUBSCRIPTION_RETRY_BASE_DELAY_MS * 2 ** (attempts - 1);
      freeSubscriptionRetryTimerRef.current = setTimeout(() => {
        // Re-arm the trigger effect for another attempt.
        freeSubscriptionAttemptSlugRef.current = null;
        setRetryVersion(version => version + 1);
      }, delay);
    }).finally(() => {
      setRetryVersion(version_0 => version_0 + 1);
    });
  }, [isLoading, isSignedIn, hasOrganization, activeOrganizationSlug, hasActiveSubscription, billingStatus, freeSubscriptionFailedSlug, ensureFreeSubscription, retryVersion]);

  // Diagnostics: an org page that was ready and drops back to the loading screen is unmounted
  // and remounted, which users see as a reload. Record why, and how long it took to recover.
  const {
    isAuthenticated: isConvexAuthenticated
  } = useConvexAuth();
  const hasBeenReadyRef = useRef(false);
  const lostAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isLoading) {
      if (lostAtRef.current !== null) {
        capturePosthogEvent("AgroProj:Auth:PageRestored", {
          durationMs: Date.now() - lostAtRef.current
        });
        lostAtRef.current = null;
      }
      hasBeenReadyRef.current = true;
      return;
    }
    if (!hasBeenReadyRef.current || lostAtRef.current !== null) return;
    lostAtRef.current = Date.now();
    capturePosthogEvent("AgroProj:Auth:PageLost", {
      reason: !isClerkLoaded ? "clerk_loading" : organizationStatus === undefined ? "status_loading" : "convex_unauthenticated",
      clerkSignedIn: Boolean(isSignedIn),
      convexAuthenticated: isConvexAuthenticated,
      visibility: document.visibilityState,
      online: navigator.onLine
    });
  }, [isLoading, isClerkLoaded, isSignedIn, isConvexAuthenticated, organizationStatus]);
  useEffect(() => {
    return () => {
      if (freeSubscriptionRetryTimerRef.current) {
        clearTimeout(freeSubscriptionRetryTimerRef.current);
      }
    };
  }, []);
  useEffect(() => {
    if (isLoading || isWaitingForFreeSubscription) return;
    if (!isSignedIn) {
      router.replace("/sign-in");
      return;
    }
    if (!hasOrganization || !activeOrganizationSlug) {
      if (!isOnboardingRoute && !isAppRoute) {
        router.replace("/onboarding/choose-organization");
      }
      return;
    }
    if (!hasActiveSubscription && isBillingRequiredRoute) {
      router.replace(buildOrganizationPath(activeOrganizationSlug, "/") as Route);
      return;
    }
    if (!hasActiveSubscription && !isUnpaidAllowedRoute) {
      router.replace(buildOrganizationPath(activeOrganizationSlug, "/") as Route);
      return;
    }
    if (hasActiveSubscription && isBillingRequiredRoute) {
      router.replace(buildOrganizationPath(activeOrganizationSlug, "/") as Route);
      return;
    }

    // Allow /onboarding/choose-organization even if user has an organization
    if (isOnboardingRoute && pathname === "/onboarding/choose-organization") {
      return;
    }

    // Redirect other onboarding routes if user already has organization
    if (isOnboardingRoute) {
      const returnTo = searchParams.get("returnTo");
      const target = returnTo ? buildOrganizationPath(activeOrganizationSlug, returnTo) : buildOrganizationPath(activeOrganizationSlug, "/");
      router.replace(target as Route);
      return;
    }
    const isWithinOrg = isPathWithinOrganization(activeOrganizationSlug, pathname);
    if (!isWithinOrg) {
      const redirectPath = buildOrganizationPath(activeOrganizationSlug, getRelativeOrganizationPath(activeOrganizationSlug, pathname));
      router.replace(redirectPath as Route);
    }
  }, [isLoading, isAuthenticated, isSignedIn, hasOrganization, activeOrganizationSlug, hasActiveSubscription, isWaitingForFreeSubscription, isBillingRequiredRoute, isUnpaidAllowedRoute, isOnboardingRoute, isAppRoute, pathname, router, searchParams]);
  if (isLoading) {
    return loadingFallback;
  }
  if (isWaitingForFreeSubscription) {
    return loadingFallback;
  }
  if (!isSignedIn) {
    return loadingFallback;
  }
  if (!hasOrganization || !activeOrganizationSlug) {
    if (isOnboardingRoute || isAppRoute) {
      return children;
    }
    return loadingFallback;
  }
  if (isOnboardingRoute && pathname === "/onboarding/choose-organization") {
    return children;
  }
  if (isOnboardingRoute) {
    return loadingFallback;
  }
  if (!hasActiveSubscription && isBillingRequiredRoute) {
    return loadingFallback;
  }
  if (!hasActiveSubscription && !isUnpaidAllowedRoute) {
    return loadingFallback;
  }
  const isWithinOrg_0 = isPathWithinOrganization(activeOrganizationSlug, pathname);
  if (!isWithinOrg_0) {
    return loadingFallback;
  }
  return <>
      {children}
      {!hasActiveSubscription && isOrganizationDashboardRoute ? <SubscriptionRequiredDialog /> : null}
    </>;
}